// Kit `.topbar` — the 56px strip above a list's tabs. Shared by the trip editor and,
// since issue #188, the template editor: both have an inline-editable title, an
// optional "view only" badge, right-aligned actions and a "⋮" menu. Everything that
// differs between them (a trip's dates + location, a template's nothing) arrives as
// the `meta` slot, so neither page owns a copy of the chrome.

import { useMemo, type CSSProperties, type ReactNode } from "react";
import { Badge, OverflowMenu, TextField, vars } from "@pack-it-up/ui-web";
import { createInlineEdit } from "@pack-it-up/viewmodel";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { CheckIcon, CancelIcon } from "../../app/icons.js";
import { affordanceButton, affordanceSave, affordanceCancel, editableHover } from "./ListTopbar.css.js";

// Kit `.edit-affordance` (lines 57–62): check (save)/cancel icon-button pair shown next
// to a field while it's being edited. `onMouseDown` calls `preventDefault` so clicking
// either button doesn't first fire the input's blur-commit handler.
export const EditAffordance = ({ onSave, onCancel }: { onSave: () => void; onCancel: () => void }): ReactNode => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
    <button
      type="button"
      aria-label="Save"
      className={[affordanceButton, affordanceSave].join(" ")}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSave}
    >
      {CheckIcon}
    </button>
    <button
      type="button"
      aria-label="Cancel"
      className={[affordanceButton, affordanceCancel].join(" ")}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onCancel}
    >
      {CancelIcon}
    </button>
  </span>
);

// Pair with the `editableField` class — the transparent idle background lives there so
// hover can win (an inline background would not be overridable by `:hover`).
export const editableFieldStyle: CSSProperties = {
  // Pointer, not the text caret: these read as buttons that open an editor, and the
  // hand is what tells you the topbar meta is clickable at all.
  cursor: "pointer",
  borderRadius: vars.radius.sm,
  padding: "2px 5px",
  margin: "-2px -5px",
  border: "none",
  font: "inherit",
  color: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
};

// The list title in the topbar — click to edit (Enter/blur save, Esc cancel, empty
// reverts — Doc 06 §4). Viewers see plain text. Kit `.topbar-title.editable`.
const TopbarTitle = ({
  name,
  label,
  canEdit,
  onRename,
}: {
  name: string;
  label: string;
  canEdit: boolean;
  onRename: (name: string) => void;
}): ReactNode => {
  const edit = useMemo(() => createInlineEdit(), []);
  const mode = useStoreValue(edit, (s) => s.mode);
  const draft = useStoreValue(edit, (s) => s.draft);

  const commit = (): void => {
    const r = edit.getState().commit();
    if (r.kind === "save") onRename(r.value);
  };

  if (canEdit && mode === "editing") {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", maxWidth: "320px" }}>
        <TextField
          label=""
          aria-label={label}
          value={draft}
          autoFocus
          onChange={(v) => edit.getState().setDraft(v)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") edit.getState().cancel();
          }}
        />
        <EditAffordance onSave={commit} onCancel={() => edit.getState().cancel()} />
      </div>
    );
  }
  return (
    <h1
      className={canEdit ? editableHover : undefined}
      style={{
        fontFamily: vars.font.heading,
        fontWeight: 700,
        fontSize: "17px",
        letterSpacing: "-0.3px",
        margin: 0,
        // Kit `.topbar-title`: shrink (not grow) + min-width:0 so it ellipsizes under
        // pressure instead of forcing the row's other flex items to shrink or overflow,
        // but doesn't stretch past its text — only the name itself should be clickable.
        flex: "0 1 auto",
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        cursor: canEdit ? "pointer" : "default",
        borderRadius: vars.radius.sm,
        padding: "3px 6px",
        marginLeft: "-6px",
      }}
      onClick={canEdit ? () => edit.getState().beginEdit("list-name", name) : undefined}
    >
      {name}
    </h1>
  );
};

export interface ListTopbarMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

export const ListTopbar = ({
  name,
  nameLabel,
  canEdit,
  isViewer,
  onRename,
  meta,
  actions,
  menuLabel,
  menuItems = [],
}: {
  name: string;
  /** aria-label for the title field, e.g. "Trip name" / "Template name". */
  nameLabel: string;
  canEdit: boolean;
  isViewer: boolean;
  onRename: (name: string) => void;
  /** Between the title and the actions: a trip's dates + location; nothing for a template. */
  meta?: ReactNode;
  /** Right-aligned topbar actions (e.g. Share). */
  actions?: ReactNode;
  menuLabel: string;
  menuItems?: ListTopbarMenuItem[];
}): ReactNode => (
  <div
    style={{
      borderBottom: `1px solid ${vars.color.border}`,
      padding: `0 ${vars.space.lg}`,
      background: vars.color.background,
      flexShrink: 0,
    }}
  >
    <div style={{ minHeight: "56px", display: "flex", alignItems: "center", gap: vars.space.md }}>
      <TopbarTitle name={name} label={nameLabel} canEdit={canEdit} onRename={onRename} />
      {isViewer && <Badge>view only</Badge>}
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: vars.space.sm, flexShrink: 0 }}>
        {actions}
        {menuItems.length > 0 && <OverflowMenu label={menuLabel} items={menuItems} />}
      </div>
    </div>
    {/* Its own row (rather than squeezed onto the title row) so it never fights the
        title and actions for width — the title row's the one guaranteed to fit. */}
    {meta !== undefined && <div style={{ paddingBottom: vars.space.md }}>{meta}</div>}
  </div>
);
